import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useCreateFleetUnitMutation,
  useUpdateFleetUnitMutation,
  type FleetModel,
  type FleetUnit,
  type FleetUnitData,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import { IconPlus } from '@shared/ui/icons';
import {
  FLEET_STATUS_LABEL,
  FLEET_UNIT_STATUSES,
  FleetStatusChip,
  fleetLeading,
  fleetType,
  formatCount,
  typo,
  unitFormValues,
  unitPayload,
  unitTitle,
  type UnitFormValues,
} from '@entities/fleet';
import { Control, ControlArea, FormGrid, FormRow } from './formFields';
import { Actions, FieldError, FieldLabel, Notice } from './ui';

const NEW = 'new';

const Intro = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

const CountNotice = styled(Notice)`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const List = styled.ul`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  padding: 0;
  list-style: none;

  &:empty {
    display: none;
  }
`;

const Card = styled.li`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const CardTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};

  & > .title {
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.base};
    color: ${({ theme }) => theme.colors.text.primary};
    overflow-wrap: anywhere;
  }

  & > .edit {
    margin-left: auto;
  }
`;

const Ids = styled.p`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const Line = styled.p`
  margin: ${({ theme }) => `${theme.spacing(1.5)} 0 0`};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;

  & > .k {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const FormBox = styled.form`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const FormTitle = styled.h4`
  margin: 0;
  font-size: ${fleetType.base};
  font-weight: 600;
`;

function UnitCard({
  unit,
  canWrite,
  onEdit,
}: {
  unit: FleetUnit;
  canWrite: boolean;
  onEdit: () => void;
}) {
  const title = unitTitle(unit);
  const ids = [
    unit.reg_number && unit.reg_number !== title ? `гос. ${unit.reg_number}` : null,
    unit.inventory_number && unit.inventory_number !== title
      ? `инв. ${unit.inventory_number}`
      : null,
    unit.vin && unit.vin !== title ? `VIN ${unit.vin}` : null,
  ].filter((s): s is string => s !== null);
  return (
    <Card>
      <CardTop>
        <span className="title">{title}</span>
        <FleetStatusChip status={unit.status} />
        {canWrite && (
          <Button
            type="button"
            className="edit"
            $variant="ghost"
            $size="sm"
            onClick={onEdit}
            aria-label={`Изменить единицу ${title}`}
          >
            Изменить
          </Button>
        )}
      </CardTop>
      {ids.length > 0 && <Ids>{ids.join(' · ')}</Ids>}
      {unit.owner_name && (
        <Line>
          <span className="k">Собственник: </span>
          {unit.owner_name}
        </Line>
      )}
      {unit.comment && <Line>{unit.comment}</Line>}
    </Card>
  );
}

const FIELDS: readonly {
  key: keyof Omit<UnitFormValues, 'status' | 'comment'>;
  label: string;
  mono?: boolean;
}[] = [
  { key: 'reg_number', label: 'Государственный номер', mono: true },
  { key: 'inventory_number', label: 'Инвентарный номер', mono: true },
  { key: 'vin', label: 'VIN или заводской номер', mono: true },
  { key: 'owner_name', label: 'Собственник' },
];

function UnitForm({
  modelId,
  unit,
  defaultOwner,
  onDone,
}: {
  modelId: string;
  unit: FleetUnit | null;
  defaultOwner: string | null;
  onDone: () => void;
}) {
  const [values, setValues] = useState<UnitFormValues>(() => unitFormValues(unit, defaultOwner));
  const [error, setError] = useState<string | null>(null);
  const [createUnit, { isLoading: creating }] = useCreateFleetUnitMutation();
  const [updateUnit, { isLoading: updating }] = useUpdateFleetUnitMutation();
  const busy = creating || updating;
  const idBase = `unit-${unit?.id ?? NEW}`;

  const set = (key: keyof UnitFormValues, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    const res = unitPayload(values);
    if ('error' in res) {
      setError(res.error);
      document.getElementById(`${idBase}-reg_number`)?.focus();
      return;
    }
    setError(null);
    const data: FleetUnitData = res.data;
    try {
      if (unit) await updateUnit({ id: unit.id, modelId, data }).unwrap();
      else await createUnit({ modelId, data }).unwrap();
      onDone();
    } catch (err) {
      setError(
        apiErrorText(err, unit ? 'Не удалось сохранить единицу.' : 'Не удалось добавить единицу.'),
      );
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLFormElement>) {
    // Esc закрывает форму единицы, а не всю карточку.
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onDone();
    }
  }

  return (
    <FormBox onSubmit={submit} onKeyDown={onKeyDown} noValidate aria-labelledby={`${idBase}-title`}>
      <FormTitle id={`${idBase}-title`}>
        {unit ? `Единица ${unitTitle(unit)}` : 'Новая единица'}
      </FormTitle>
      <FormGrid>
        {FIELDS.map((f) => (
          <FormRow key={f.key}>
            <FieldLabel htmlFor={`${idBase}-${f.key}`}>{f.label}</FieldLabel>
            <Control
              id={`${idBase}-${f.key}`}
              value={values[f.key]}
              autoComplete="off"
              spellCheck={f.mono ? false : undefined}
              aria-invalid={error && f.key !== 'owner_name' ? true : undefined}
              aria-describedby={error ? `${idBase}-error` : undefined}
              onChange={(e) => set(f.key, e.target.value)}
            />
          </FormRow>
        ))}
        <FormRow>
          <FieldLabel htmlFor={`${idBase}-status`}>Статус</FieldLabel>
          <Select
            id={`${idBase}-status`}
            value={values.status}
            onChange={(e) => set('status', e.target.value)}
          >
            {FLEET_UNIT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {FLEET_STATUS_LABEL[s]}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow $wide>
          <FieldLabel htmlFor={`${idBase}-comment`}>Комментарий</FieldLabel>
          <ControlArea
            id={`${idBase}-comment`}
            value={values.comment}
            rows={2}
            onChange={(e) => set('comment', e.target.value)}
          />
        </FormRow>
      </FormGrid>
      {error && (
        <FieldError id={`${idBase}-error`} role="alert">
          {error}
        </FieldError>
      )}
      <Actions>
        <Button type="submit" $variant="primary" $size="sm" disabled={busy}>
          {busy ? 'Сохраняю…' : unit ? 'Сохранить' : 'Добавить'}
        </Button>
        <Button type="button" $variant="ghost" $size="sm" onClick={onDone} disabled={busy}>
          Отмена
        </Button>
      </Actions>
    </FormBox>
  );
}

/**
 * Единицы техники модели (ТЗ п. 6.1.2): для ПРЦ хватает количества в
 * карточке, список нужен, когда важно, какая именно машина и чья.
 */
export function UnitsPanel({
  model,
  units,
  canWrite,
}: {
  model: FleetModel;
  units: readonly FleetUnit[];
  canWrite: boolean;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const inService = units.filter((u) => u.status === 'active' || u.status === 'reserve').length;
  const archived = model.status === 'archived';

  function done() {
    setEditing(null);
    requestAnimationFrame(() => addRef.current?.focus());
  }

  return (
    <>
      <Intro>
        {typo(
          'Для ПРЦ и средней ставки группы достаточно количества единиц в карточке. Список единиц нужен, когда важно, какая машина и чья: госномер, инвентарный номер, VIN.',
        )}
      </Intro>
      {units.length > 0 && inService !== model.units_count && (
        <CountNotice $tone="info" role="note">
          <span className="text">
            {typo(
              `В карточке указано ${formatCount(model.units_count)} ед., в списке в работе и в резерве — ${formatCount(inService)}.`,
            )}
          </span>
        </CountNotice>
      )}
      <List aria-label="Единицы техники">
        {units.map((u) =>
          editing === u.id ? (
            <li key={u.id}>
              <UnitForm modelId={model.id} unit={u} defaultOwner={model.owner_name} onDone={done} />
            </li>
          ) : (
            <UnitCard
              key={u.id}
              unit={u}
              canWrite={canWrite && editing === null}
              onEdit={() => setEditing(u.id)}
            />
          ),
        )}
      </List>
      {units.length === 0 && editing !== NEW && <Intro>Единицы не заведены.</Intro>}
      {editing === NEW ? (
        <UnitForm modelId={model.id} unit={null} defaultOwner={model.owner_name} onDone={done} />
      ) : (
        canWrite &&
        !archived &&
        editing === null && (
          <Button ref={addRef} type="button" $variant="secondary" onClick={() => setEditing(NEW)}>
            <IconPlus size={16} />
            Добавить единицу
          </Button>
        )
      )}
    </>
  );
}
